import { useState, useEffect, useCallback } from "react";
import CustomAlert from "../Common/CustomAlert"
import Loading from "../Common/Loading"
import saven from "../Common/Images/save_n.gif"
import saveo from "../Common/Images/save_o.gif"
import canceln from "../Common/Images/cancel_n.gif"
import cancelo from "../Common/Images/cancel_o.gif"

interface Product {
  ProdID: number;
  ProdDesc: string;
  Subject: string;
}

interface FamilyForm {
  first_name: string;
  middle_initial: string;
  family_name: string;
  login: string;
  password: string;
  email: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip_code: string;
  phone: string;
  mobile: string;
  max_children: string;
  account_type: string;
  activity_status: string;
  expiry_date: string;
  md_licenses: string;
  send_brick_report: boolean;
  send_kg_report: boolean;
  send_wp_report: boolean;
  gets_default_lesson_plan: boolean;
  gets_default_lesson_plan_english: boolean;
  eligible_for_incentives: boolean;
  can_access_lesson_plan: boolean;
  can_earn_points: boolean;
  selected_products: number[];
}


const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;
// const SCHOOL_CODE = "DLR_KE_AU";
const SCHOOL_CODE = sessionStorage.getItem("SelectedSchoolCode") || "DLR_KE_AU";

const DEFAULT_FORM: FamilyForm = {
  first_name: "",
  middle_initial: "",
  family_name: "",
  login: "",
  password: "",
  email: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip_code: "",
  phone: "",
  mobile: "",
  max_children: "3",
  account_type: "N",
  activity_status: "A",
  expiry_date: "31/12/2050",
  md_licenses: "",
  send_brick_report: true,
  send_kg_report: true,
  send_wp_report: true,
  gets_default_lesson_plan: true,
  gets_default_lesson_plan_english: true,
  eligible_for_incentives: true,
  can_access_lesson_plan: true,
  can_earn_points: true,
  selected_products: [],
};
document.body.style.backgroundColor = "azure";

function Toast({
  msg,
  type,
  onClose,
}: {
  msg: string;
  type: "success" | "error" | "info";
  onClose: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors: Record<string, string> = {
    success: "#22c55e",
    error: "#ef4444",
    info: "#3b82f6",
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 32,
        right: 32,
        background: colors[type],
        color: "#fff",
        padding: "14px 22px",
        borderRadius: 10,
        fontFamily: "Trebuchet MS",
        fontSize: 14,
        fontWeight: 500,
        boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
        zIndex: 9999,
        maxWidth: 380,
        lineHeight: 1.5,
        cursor: "pointer",
        animation: "slideUp 0.25s ease",
      }}
      onClick={onClose}
    >
      {msg}
    </div>
  );
}


function FieldRow({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "200px 1fr",
        alignItems: "center",
        borderBottom: "1px solid #e2e8f0",
        minHeight: 30,
      }}
    >
      <div
        style={{
          padding: "6px 6px",
          background: required ? "#000080" : "#5588bb",
          color: "#fff8ed",
          fontSize: 12.5,
          fontFamily: "Trebuchet MS",
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          gap: 4,
          minHeight: 30,
          borderRight: "2px solid #fff",
        }}
      >
        {label}
        {required && <span style={{ color: "#f87171" }}>*</span>}
      </div>
      <div
        style={{
          // padding: "5px 10px",
          background: "#f8fafc",
          minHeight: 30,
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "5px 20px",
  border: "1px solid #000000",
  borderRadius: 0,
  fontSize: 13,
  fontFamily: "Trebuchet MS",
  background: "#fff",
  color: "#1e293b",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.15s",
};

const checkStyle: React.CSSProperties = {
  accentColor: "#1e3a5f",
  width: 15,
  height: 15,
  cursor: "pointer",
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function RegisterFamily() {
  const [form, setForm] = useState<FamilyForm>(DEFAULT_FORM);
  const [states, setStates] = useState<string[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [editLogin, setEditLogin] = useState<string | null>(null); // null = register mode
  const [loginChecked, setLoginChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveHover, setSaveHover] = useState(false);
  const [cancelHover, setCancelHover] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    message: "",
  });

  const showAlert = (message: string) => {
    setAlertInfo({
      show: true,
      message,
    });
  };

  // ── Load states + products on mount ──
  useEffect(() => {
    document.body.style.backgroundColor = "azure";

    setLoading(true);

    Promise.all([
      fetch(`${API_BASE}api/family/states/?school_code=${SCHOOL_CODE}`)
        .then((r) => r.json())
        .then((d) => setStates(d.states || [])),

      fetch(`${API_BASE}api/family/products/?school_code=${SCHOOL_CODE}`)
        .then((r) => r.json())
        .then((d) => setProducts(d.products || []))
    ])
      .catch(() => showAlert("Failed to load page data."))
      .finally(() => setLoading(false));

    const params = new URLSearchParams(window.location.search);
    const loginParam =
      params.get("Login") || params.get("login");

    if (loginParam) {
      setEditLogin(loginParam);
      loadFamily(loginParam);
    }
  }, []);

  const loadFamily = (login: string) => {
    setLoading(true);
    fetch(`${API_BASE}api/family?login=${login}&school_code=${SCHOOL_CODE}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.family) {
          const f = d.family;
          setForm({
            first_name: f.first_name,
            middle_initial: f.middle_initial,
            family_name: f.family_name,
            login: f.login,
            password: f.password,
            email: f.email,
            address1: f.address1,
            address2: f.address2,
            city: f.city,
            state: f.state,
            zip_code: f.postal_code,
            phone: f.phone,
            mobile: f.mobile,
            max_children: String(f.max_children),
            account_type: f.account_type,
            activity_status: f.activity_status,
            expiry_date: f.expiry_date,
            md_licenses: f.md_licenses ? String(f.md_licenses) : "",
            send_brick_report: f.send_brick_report,
            send_kg_report: f.send_kg_report,
            send_wp_report: f.send_wp_report,
            gets_default_lesson_plan: f.gets_default_lesson_plan,
            gets_default_lesson_plan_english: f.gets_default_lesson_plan_english,
            eligible_for_incentives: f.eligible_for_incentives,
            can_access_lesson_plan: f.can_access_lesson_plan,
            can_earn_points: f.can_earn_points,
            selected_products: f.assigned_products,
          });
          setLoginChecked(true);
        }
      })
      .catch(() => showAlert("Failed to load family details."))
      .finally(() => setLoading(false));
  };

  const set = (key: keyof FamilyForm, val: unknown) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  // ── Check Login ──
  const handleCheckLogin = async () => {
    if (!form.login.trim()) {
      showAlert("Please enter a Login ID.");
      return;
    }
    if (!/^[A-Za-z0-9_\-\.@]+$/.test(form.login)) {
      showAlert("Login ID should contain Alphabets, Numbers, -, _ only.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}api/family/check-login/?login=${form.login}&school_code=${SCHOOL_CODE}`
      );
      const data = await res.json();
      if (data.available) {
        setLoginChecked(true);
        showAlert(data.message);
      } else {
        setLoginChecked(false);
        showAlert(data.message);
      }
    } catch {
      showAlert("Login check failed.");
    } finally {
      setLoading(false);
    }
  };

  // ── Toggle product selection ──
  const toggleProduct = (id: number) => {
    setForm((prev) => ({
      ...prev,
      selected_products: prev.selected_products.includes(id)
        ? prev.selected_products.filter((p) => p !== id)
        : [...prev.selected_products, id],
    }));
  };

  // ── Validate ──
  const validate = (): boolean => {
    if (!form.first_name) { showAlert("First Name is required."); return false; }
    if (!form.family_name) { showAlert("Family Name is required."); return false; }
    if (!form.login) { showAlert("Login ID is required."); return false; }
    if (!editLogin && !loginChecked) { showAlert("Please check Login availability first."); return false; }
    if (!form.password) { showAlert("Password is required."); return false; }
    if (!form.max_children || isNaN(Number(form.max_children))) {
      showAlert("Max Children must be a valid number.");
      return false;
    }
    if (Number(form.max_children) > 10) { showAlert("Max Children cannot exceed 10."); return false; }
    if (!form.expiry_date) { showAlert("Account Valid Upto date is required."); return false; }
    if (!form.state) { showAlert("Please select a State."); return false; }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      showAlert("Please enter a valid email address.");
      return false;
    }
    return true;
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!validate()) return;

    // Email dup check
    if (form.email) {
      const emailRes = await fetch(
        `${API_BASE}api/family/check-email/?email=${encodeURIComponent(form.email)}&login=${form.login}&school_code=${SCHOOL_CODE}`
      );
      const emailData = await emailRes.json();
      if (emailData.status === "DUP") {
        if (!window.confirm("Email address already exists in another account.\n\nDo you still want to proceed?")) {
          return;
        }
      }
    }

    const body = {
      school_code: SCHOOL_CODE,
      ...form,
      product_ids: form.selected_products,
    };

    setLoading(true);
    try {
      const isEdit = !!editLogin;
      const res = await fetch(`${API_BASE}api/family/${isEdit ? "update" : "register"}/`, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        showAlert(data.message);
        if (!isEdit) {
          setEditLogin(form.login);
        }
      } else {
        showAlert(data.error || "An error occurred.");
      }
    } catch {
      showAlert("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Cancel ──
  const handleCancel = () => {
    window.history.back();
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────

  const isEdit = !!editLogin;
  const pageTitle = isEdit ? "Edit Family Details" : "Register Family";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: azure; font-family: 'DM Sans', sans-serif; }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        input:focus, select:focus {
          border-color: #1e3a5f !important;
          box-shadow: 0 0 0 3px rgba(30,58,95,0.12);
        }
        .prod-row:hover { background: #eff6ff !important; }
        .prod-row.selected { background: #dbeafe !important; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 3px; }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          background: "azure",
          width: "100%",
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "5px 10px",
          animation: "fadeIn 0.4s ease",
        }}
      >
        <h3 style={{ textAlign: "center", color: "navy", marginBottom: "5px", fontFamily: "Arial" }}> {SCHOOL_CODE}</h3>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "2%", marginBottom: "5px", marginRight: "5px", marginTop: "0px" }}>
          <a href="/SUPPORT/DealerDashboard" style={{ textDecoration: 'underline', color: 'blue', fontWeight: 'bolder', fontFamily: "Arial", fontSize: "14px" }}>Back To Menu</a>
          <a href="/SUPPORT/SupportLoggedOut" style={{ textDecoration: 'underline', color: 'blue', fontWeight: 'bolder', fontFamily: "Arial", fontSize: "14px" }}>Logout</a>
        </div>
        {/* ── Header Bar ── */}
        <div
          style={{
            background: "#2f6089",
            borderRadius: "0",
            padding: "0 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: 42,
            boxShadow: "0 4px 20px rgba(15,39,68,0.18)",
            border: "1px solid black"
          }}
        >
          <h1
            style={{
              color: "#ffffff",
              fontSize: 17,
              fontWeight: 600,
              fontFamily: "Trebuchet MS",
              letterSpacing: 0.3,
            }}
          >
            {/* Kinetic Education &mdash;{" "} */}
            <span style={{ color: "#ffffff", textAlign: "center" }}>{pageTitle}</span>
          </h1>

          <div style={{ display: "flex", gap: 10 }}>
            <img
              src={saveHover ? saveo : saven}
              alt="Save"
              onClick={!loading ? handleSubmit : undefined}
              onMouseEnter={() => setSaveHover(true)}
              onMouseLeave={() => setSaveHover(false)}
              style={{
                cursor: loading ? "not-allowed" : "pointer"
              }}
            />
            <img
              src={cancelHover ? cancelo : canceln}
              alt="Cancel"
              onClick={handleCancel}
              onMouseEnter={() => setCancelHover(true)}
              onMouseLeave={() => setCancelHover(false)}
              style={{
                cursor: "pointer"
              }}
            />
          </div>
        </div>

        {/* ── Main Content ── */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderTop: "none",
            borderRadius: "0 0 12px 12px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            minHeight: 540,
            boxShadow: "0 8px 32px rgba(15,39,68,0.08)",
          }}
        >
          {/* ── Left Panel: Family Login Details ── */}
          <div
            style={{
              borderRight: "1px solid #e2e8f0",
              overflowY: "auto",
              maxHeight: "75vh",
            }}
          >
            {/* Section header */}
            <div
              style={{
                background: "#2f6089",
                color: "#ffffff",
                textAlign: "center",
                padding: "4px 0",
                fontSize: 12,
                fontWeight: 400,
                letterSpacing: 1,
              }}
            >
              Family Login Details
            </div>

            <FieldRow label="First Name" required>
              <input
                style={inputStyle}
                value={form.first_name}
                maxLength={30}
                onChange={(e) => set("first_name", e.target.value)}
                placeholder="Enter first name"
              />
            </FieldRow>

            <FieldRow label="Middle Initial">
              <input
                style={inputStyle}
                value={form.middle_initial}
                maxLength={10}
                onChange={(e) => set("middle_initial", e.target.value)}
              />
            </FieldRow>

            <FieldRow label="Family Name" required>
              <input
                style={inputStyle}
                value={form.family_name}
                maxLength={30}
                onChange={(e) => set("family_name", e.target.value)}
                placeholder="Enter family/last name"
              />
            </FieldRow>

            <FieldRow label="Login ID" required>
              <div style={{ display: "flex", gap: 8, width: "100%" }}>
                <input
                  style={{ ...inputStyle, flex: 1 }}
                  value={form.login}
                  maxLength={50}
                  readOnly={isEdit}
                  onChange={(e) => { set("login", e.target.value); setLoginChecked(false); }}
                  placeholder="Enter login ID"
                />
                {!isEdit && (
                  <button
                    onClick={handleCheckLogin}
                    style={{
                      backgroundColor: "#efefef",
                      color: "#000000",
                      border: "1px solid #767676",
                      borderRadius: 5,
                      padding: "6px 14px",
                      fontSize: 11.5,
                      fontWeight: 700,
                      cursor: "pointer",
                      letterSpacing: 0.5,
                      whiteSpace: "nowrap",

                    }}
                  >
                    {loginChecked ? "✓ OK" : "CHECK"}
                  </button>
                )}
              </div>
            </FieldRow>

            <FieldRow label="Password" required>
              <input
                style={inputStyle}
                type="password"
                value={form.password}
                maxLength={50}
                onChange={(e) => set("password", e.target.value)}
                placeholder="Enter password"
              />
            </FieldRow>

            <FieldRow label="Account Type">
              <select
                style={inputStyle}
                value={form.account_type}
                onChange={(e) => set("account_type", e.target.value)}
              >
                <option value="N">Normal</option>
                <option value="E">Educational Consultant</option>
                <option value="T">Trial Account</option>
              </select>
            </FieldRow>

            <FieldRow label="Max. Children" required>
              <input
                style={{ ...inputStyle, width: 80 }}
                value={form.max_children}
                maxLength={2}
                onChange={(e) => {
                  if (/^\d*$/.test(e.target.value)) set("max_children", e.target.value);
                }}
              />
              {isEdit && (
                <a
                  style={{
                    color: "blue",
                    fontSize: "14px",
                    padding: "5px",
                    marginLeft: "5px",
                    fontFamily: "Trebuchet MS",
                    textDecoration: "underline",
                    cursor: "pointer"

                  }}
                  onClick={() =>
                    window.location.href =
                    `/DEALER/EL_ListFamilyUsers?Login=${form.login}`
                  }
                >
                  Show Family Users
                </a>
              )}
            </FieldRow>

            <FieldRow label="A/c. Valid Upto" required>
              <input
                style={{ ...inputStyle, width: 160, fontFamily: "Trebuchet MS" }}
                value={form.expiry_date}
                maxLength={10}
                placeholder="dd/MM/yyyy"
                onChange={(e) => set("expiry_date", e.target.value)}
              />
            </FieldRow>

            <FieldRow label="Status">
              <div style={{ display: "flex", gap: 20 }}>
                {[
                  { value: "A", label: "Active" },
                  { value: "I", label: "InActive" },
                ].map((status) => (
                  <label
                    key={status.value}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={status.value}
                      checked={form.activity_status === status.value}
                      onChange={() => set("activity_status", status.value)}
                      style={{
                        accentColor: "#1e3a5f",
                        cursor: "pointer",
                      }}
                    />
                    {status.label}
                  </label>
                ))}
              </div>
            </FieldRow>

            <FieldRow label="Email">
              <input
                style={inputStyle}
                type="email"
                value={form.email}
                maxLength={200}
                onChange={(e) => set("email", e.target.value)}
                placeholder="name@example.com"
              />
            </FieldRow>

            <FieldRow label="Address Line-1">
              <input
                style={inputStyle}
                value={form.address1}
                maxLength={60}
                onChange={(e) => set("address1", e.target.value)}
              />
            </FieldRow>

            <FieldRow label="Address Line-2">
              <input
                style={inputStyle}
                value={form.address2}
                maxLength={60}
                onChange={(e) => set("address2", e.target.value)}
              />
            </FieldRow>

            <FieldRow label="City / State">
              <div style={{ display: "flex", gap: 6, width: "100%", alignItems: "center" }}>
                <input
                  style={{ ...inputStyle, flex: 1 }}
                  value={form.city}
                  maxLength={50}
                  onChange={(e) => set("city", e.target.value)}
                  placeholder="City"
                />
                <span style={{ color: "#94a3b8", fontSize: 13 }}>/</span>
                <select
                  style={{ ...inputStyle, flex: 1 }}
                  value={form.state}
                  onChange={(e) => set("state", e.target.value)}
                >
                  <option value="">--Select--</option>
                  {states.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </FieldRow>

            <FieldRow label="Zip Code">
              <input
                style={{ ...inputStyle, width: 120, fontFamily: "Trebuchet MS" }}
                value={form.zip_code}
                maxLength={6}
                onChange={(e) => {
                  if (/^\d*$/.test(e.target.value)) set("zip_code", e.target.value);
                }}
              />
            </FieldRow>

            <FieldRow label="Phone# / Mobile#">
              <div style={{ display: "flex", gap: 6, width: "100%", alignItems: "center" }}>
                <input
                  style={{ ...inputStyle, flex: 1, fontFamily: "Trebuchet MS" }}
                  value={form.phone}
                  maxLength={20}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="Phone"
                />
                <span style={{ color: "#94a3b8" }}>/</span>
                <input
                  style={{ ...inputStyle, flex: 1, fontFamily: "Trebuchet MS" }}
                  value={form.mobile}
                  maxLength={10}
                  onChange={(e) => {
                    if (/^\d*$/.test(e.target.value)) set("mobile", e.target.value);
                  }}
                  placeholder="Mobile"
                />
              </div>
            </FieldRow>

            <FieldRow label="Eligible For Incentives">
              <input
                type="checkbox"
                style={checkStyle}
                checked={form.eligible_for_incentives}
                onChange={(e) => set("eligible_for_incentives", e.target.checked)}
              />
            </FieldRow>

            <FieldRow label="Default Lesson Plan">
              <div style={{ display: "flex", gap: 18 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" style={checkStyle} checked={form.gets_default_lesson_plan} onChange={(e) => set("gets_default_lesson_plan", e.target.checked)} />
                  Mathematics
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" style={checkStyle} checked={form.gets_default_lesson_plan_english} onChange={(e) => set("gets_default_lesson_plan_english", e.target.checked)} />
                  English
                </label>
              </div>
            </FieldRow>

            <FieldRow label="MD Licenses, Reports">
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <input
                  style={{ ...inputStyle, width: 50, fontFamily: "Trebuchet MS" }}
                  value={form.md_licenses}
                  maxLength={2}
                  onChange={(e) => {
                    if (/^\d*$/.test(e.target.value)) set("md_licenses", e.target.value);
                  }}
                />
                {[
                  { key: "send_brick_report", label: "Brick Wall" },
                  { key: "send_kg_report", label: "K.Gap" },
                  { key: "send_wp_report", label: "Study Plan" },
                ].map(({ key, label }) => (
                  <label key={key} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      style={checkStyle}
                      checked={form[key as keyof FamilyForm] as boolean}
                      onChange={(e) => set(key as keyof FamilyForm, e.target.checked)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </FieldRow>

            <FieldRow label="Family Users Can">
              <div style={{ display: "flex", gap: 18 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" style={checkStyle} checked={form.can_access_lesson_plan} onChange={(e) => set("can_access_lesson_plan", e.target.checked)} />
                  Access Lesson Plan
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" style={checkStyle} checked={form.can_earn_points} onChange={(e) => set("can_earn_points", e.target.checked)} />
                  Earn Points
                </label>
              </div>
            </FieldRow>

            {/* Mandatory fields note
            <div style={{ padding: "4px 14px", fontSize: 11.5, color: "#64748b" }}>
              <span style={{ color: "#ef4444" }}>* </span>Mandatory fields
            </div> */}
          </div>

          {/* ── Right Panel: Products ── */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div
              style={{
                background: "#2f6089",
                color: "#ffffff",
                textAlign: "center",
                padding: "4px 0",
                fontSize: 12,
                fontWeight: 400,
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Products
            </div>

            {/* Product table header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "40px 60px 1fr",
                background: "#c7ddfd",
                border: "1px solid #a3c0e8",
                padding: "8px 12px",
                fontSize: 13,
                fontWeight: 500,
                color: "#2859ac",
                letterSpacing: 0.5,
              }}
            >
              <div></div>
              <div>ProdID</div>
              <div>ProdDesc</div>
            </div>

            <div style={{ overflowY: "auto", flex: 1, maxHeight: "65vh" }}>
              {products.length === 0 ? (
                <div
                  style={{
                    padding: 40,
                    textAlign: "center",
                    color: "#000",
                    fontSize: 13,
                    fontStyle: "italic",
                  }}
                >
                  No products available for this school.
                </div>
              ) : (
                products.map((prod, index) => {
                  const sel = form.selected_products.includes(prod.ProdID);
                  const isEven = index % 2 === 0;

                  return (
                    <div
                      key={prod.ProdID}
                      className={`prod-row${sel ? " selected" : ""}`}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "40px 60px 1fr",
                        alignItems: "center",
                        padding: "10px 12px",
                        // borderBottom: "1px solid #e2e8f0",
                        cursor: "pointer",
                        background: sel
                          ? "#dbeafe"
                          : isEven
                            ? "#f4faff"
                            : "#ffffff",
                        transition: "background 0.12s",

                      }}
                      onClick={() => toggleProduct(prod.ProdID)}
                    >
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <input
                          type="checkbox"
                          style={checkStyle}
                          checked={sel}
                          readOnly
                        />
                      </div>

                      <div
                        style={{
                          fontFamily: "Trebuchet MS",
                          fontSize: 13,
                          color: "#0f0f0f",
                          fontWeight: 500,
                          borderRight: "#000000",
                        }}
                      >
                        {prod.ProdID}
                      </div>

                      <div style={{ fontSize: 13, color: "#1e293b" }}>
                        {prod.ProdDesc}
                        {prod.Subject && (
                          <span
                            style={{
                              marginLeft: 8,
                              fontSize: 10.5,
                              background: "#eff6ff",
                              color: "#3b82f6",
                              padding: "1px 7px",
                              borderRadius: 20,
                              fontWeight: 500,
                              border: "1px solid #bfdbfe",
                            }}
                          >
                            {prod.Subject}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {form.selected_products.length > 0 && (
              <div
                style={{
                  padding: "10px 16px",
                  borderTop: "1px solid #e2e8f0",
                  background: "#f0f9ff",
                  fontSize: 12.5,
                  color: "#0369a1",
                  fontWeight: 500,
                }}
              >
                {form.selected_products.length} product{form.selected_products.length !== 1 ? "s" : ""} selected
              </div>
            )}
          </div>
        </div>
      </div>

      {loading && <Loading />}

      {alertInfo.show && (
        <CustomAlert
          message={alertInfo.message}
          onClose={() =>
            setAlertInfo({
              show: false,
              message: "",
            })
          }
        />
      )}
    </>
  );
}